// SAFE-WRITE-BEGIN: frontend/src/pages/valuation/_ValueForecast.tsx
/**
 * M-DEMO — the 10-year Forecast. Today's headline value compounded at the rate
 * the owner picks: three tiles, a slider plus a typed box, and three paths
 * (Expected gold, Conservative rate−3 dashed blue, Strong rate+3 dashed green)
 * with the band between them. The slider redraws live on the client; the rate
 * is saved only on release (or Enter in the box). Compare methods are not drawn.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  Area, CartesianGrid, ComposedChart, Line, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  GROWTH_MAX, GROWTH_MIN, GROWTH_STEP, MD, forecastChartData, millions, parseGrowth, scenarioRates,
} from './_valueMethods'

const GOLD = '#c9a24a'
const BLUE = '#3b6fb6'
const GREEN = '#16a34a'
const NAVY = '#0d1f3c'

function Tile({ label, value, className, style }: {
  label: string; value: string; className: string; style?: CSSProperties
}) {
  return (
    <div role="group" aria-label={label} className={`rounded-lg p-4 ${className}`} style={style}>
      <div className="text-sm font-medium">{label}</div>
      <div className="mt-1 text-3xl font-bold tracking-tight">{value}</div>
    </div>
  )
}

/** Recharts label: the year-10 value at the end of a path only. */
function endLabel(color: string) {
  return function EndLabel(p: { x?: number | string; y?: number | string; index?: number; value?: unknown }) {
    if (p.index !== 10 || typeof p.value !== 'number') return null
    return (
      <text x={Number(p.x) + 8} y={Number(p.y)} dy={4} fill={color} fontSize={14} fontWeight={700}>
        {millions(p.value)}
      </text>
    )
  }
}

function ForecastTooltip({ active, payload, label }: {
  active?: boolean; label?: string | number
  payload?: ReadonlyArray<{ dataKey?: unknown; value?: unknown }>
}) {
  if (!active || !payload?.length) return null
  const rows = payload.filter(p => typeof p.value === 'number')
  const names: Record<string, string> = { expected: MD.expected, conservative: MD.conservative, strong: MD.strong }
  return (
    <div className="rounded border border-pal-line bg-pal-white px-3 py-2 text-sm shadow">
      <div className="font-semibold text-pal-ink">{label}</div>
      {rows.map(p => (
        <div key={String(p.dataKey)} className="text-pal-ink-body">
          {names[String(p.dataKey)]}: {millions(p.value as number)}
        </div>
      ))}
    </div>
  )
}

export function ValueForecast({ today, initialRate, error, onCommit }: {
  today: number
  initialRate: number
  /** The server refused the rate (FORECAST_GROWTH_OUT_OF_RANGE). */
  error: boolean
  onCommit: (rate: number) => void
}) {
  const [rate, setRate] = useState(initialRate)
  const [typed, setTyped] = useState(String(initialRate))
  const [badTyped, setBadTyped] = useState(false)

  useEffect(() => { setRate(initialRate); setTyped(String(initialRate)) }, [initialRate])

  const rows = useMemo(() => forecastChartData(today, rate), [today, rate])
  const r = scenarioRates(rate)
  const year10 = rows[10].expected
  const growth = year10 - today
  const lowest = Math.min(today, ...rows.map(x => x.conservative))
  const highest = Math.max(...rows.map(x => x.strong))
  // The axis starts just below today's value (or the lowest path, if it falls).
  const yMin = Math.max(0, Math.floor((lowest * 0.95) / 50_000) * 50_000)
  const yMax = Math.ceil((highest * 1.05) / 50_000) * 50_000

  const commit = (next: number) => { setBadTyped(false); onCommit(next) }

  const slide = (v: string) => { const n = Number(v); setRate(n); setTyped(String(n)); setBadTyped(false) }

  const enterTyped = () => {
    const n = parseGrowth(typed)
    if (n === null) { setBadTyped(true); return }
    setRate(n)
    commit(n)
  }

  return (
    <section className="rounded-lg border border-pal-line bg-pal-white p-5">
      <h2 className="text-lg font-semibold text-pal-navy">{MD.forecastTitle}</h2>
      <p className="mt-1 text-sm text-pal-ink-soft">{MD.forecastNote}</p>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Tile label={MD.tileToday} value={millions(today)} className="text-white" style={{ backgroundColor: NAVY }} />
        <Tile
          label={MD.tileYear10(rate)}
          value={millions(year10)}
          className="text-[#0d1f3c]"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #ecd089)` }}
        />
        <Tile
          label={MD.tileGrowth}
          value={`${growth >= 0 ? '+' : ''}${millions(growth)}`}
          className="border border-green-200 bg-green-50 text-green-800"
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <input
          type="range"
          aria-label={MD.slider}
          min={GROWTH_MIN}
          max={GROWTH_MAX}
          step={GROWTH_STEP}
          value={rate}
          onChange={e => slide(e.target.value)}
          onPointerUp={() => commit(rate)}
          onKeyUp={e => { if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') commit(rate) }}
          className="min-w-[200px] flex-1 accent-[#c9a24a]"
        />
        <span className="rounded-full px-3 py-1 text-sm font-bold text-pal-navy" style={{ backgroundColor: `${GOLD}33` }}>
          {`${rate}%`}
        </span>
        <label className="flex items-center gap-2 text-sm text-pal-ink-body">
          {MD.growthBox}
          <input
            type="number"
            min={GROWTH_MIN}
            max={GROWTH_MAX}
            step={GROWTH_STEP}
            value={typed}
            onChange={e => { setTyped(e.target.value); setBadTyped(false) }}
            onKeyDown={e => { if (e.key === 'Enter') enterTyped() }}
            className="w-24 rounded border border-pal-line-strong px-2 py-1"
          />
        </label>
      </div>
      {(badTyped || error) && <p className="mt-2 text-sm text-pal-warning-text">{MD.growthOutOfRange}</p>}

      <div className="mt-5 h-[360px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 16, right: 80, bottom: 8, left: 8 }}>
            <defs>
              <linearGradient id="mdemo-band" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} stopOpacity={0.22} />
                <stop offset="100%" stopColor={BLUE} stopOpacity={0.22} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 13, fill: '#374151' }} />
            <YAxis
              domain={[yMin, yMax]}
              tickFormatter={(v: number) => millions(v)}
              tick={{ fontSize: 13, fill: '#374151' }}
              width={72}
            />
            <Tooltip content={<ForecastTooltip />} />
            <Area dataKey="band" stroke="none" fill="url(#mdemo-band)" isAnimationActive={false} />
            <Line dataKey="conservative" stroke={BLUE} strokeWidth={2} strokeDasharray="6 4" dot={false}
              isAnimationActive={false} label={endLabel(BLUE)} />
            <Line dataKey="strong" stroke={GREEN} strokeWidth={2} strokeDasharray="6 4" dot={false}
              isAnimationActive={false} label={endLabel(GREEN)} />
            <Line dataKey="expected" stroke={GOLD} strokeWidth={4} dot={false}
              isAnimationActive={false} label={endLabel(GOLD)} />
            <ReferenceDot x={MD.today} y={today} r={6} fill={NAVY} stroke="white" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-pal-ink-body">
        <span><span className="font-bold" style={{ color: GOLD }}>━ {MD.expected}</span> <span>{`${r.expected}%/yr`}</span></span>
        <span><span className="font-bold" style={{ color: BLUE }}>┅ {MD.conservative}</span> <span>{`${r.conservative}%/yr`}</span></span>
        <span><span className="font-bold" style={{ color: GREEN }}>┅ {MD.strong}</span> <span>{`${r.strong}%/yr`}</span></span>
      </div>
      <p className="mt-1 text-xs text-pal-ink-muted">{MD.band}</p>
    </section>
  )
}
// SAFE-WRITE-EOF: frontend/src/pages/valuation/_ValueForecast.tsx
